import lodash from 'lodash';
import { browserHistory } from 'react-router';

import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import { mutateGraphQL, queryGraphQL } from 'common/util/query';
import { node, nodeWithArgs, toMutation, toQuery } from 'common/util/queryHelper';
import { userDefaultInstitutionIdSelector } from 'common/selectors';
import { getStringValue } from 'common/util/dataTypeValues';
import { activateStudy } from './createStudy';
import { STUDY_USER_TYPE } from 'components/common/Study/StudyTeamMemberForm';
import { getStudyTeamChanges, getStudyMetadataChanges } from '../selectors/getStudyChanges';


export const LOAD_FULL_STUDY_OBJECT = 'LOAD_FULL_STUDY_OBJECT';


/**
 * Loads an existing study along with its sub components
 * @param {String} studyId 
 */
export function loadStudy(studyId) {
    return async dispatch => {
        const query = nodeWithArgs('study', {
            studyId
        }, [
            'id',
            'name',
            'binderId',
            'studyTypeId',
            'institutionId',
            'currentStageId',
            node('studyType', [
                'id',
                'name',
                'category',
                'institutionId',
                'binderTemplateId',
            ]),
            node('studyTeam', [
                'id',
                'userId',
                'studyId',
                'studyRoleId',
                'institutionId',
                'isClinicalStaff',
                'hasAdminRole',
                'nameOfResearcher',
                'studyUserType',
                node('user', [
                    'sub',
                    'displayName',
                ]),
                node('studyRole', [
                    'id',
                    'name',
                ]),
            ]),
            node('metadata', [
                'id',
                'name',
                'type',
                'value',
                'studyId',
                'dataType',
                'descriptorId',
                'institutionId',
                'inputFieldType',
            ]),
            node('currentStage', [
                'id',
                'name',
            ]),
        ]);

        const response = await queryGraphQL(toQuery(query));
        if (response && response.errors) {
            dispatch(addApplicationMessage('error', 'Failed to load study. Please try again later.'));
        } else {
            dispatch({
                type: LOAD_FULL_STUDY_OBJECT,
                payload: response.data.study
            });
        }
    };
}



/**
 * Compares existing study data with data entered in the workflow. 
 * If any changes are found, they a persisted.
 * @param {String} existingStudyId 
 * @param {String} status The status the study will be after this saving
 * @param {String} [nextLocation] Next URL to push user 
 */
export function updateStudy(existingStudyId, status, nextLocation) {
    return async (dispatch, getState) => {
        const storeState = getState();

        const studyResponse = await updateStudyMutation(storeState, existingStudyId);
        validateResolvedMutation(studyResponse, dispatch);

        const studyTeamResponse = await updateStudyTeam(storeState, existingStudyId);
        validateResolvedMutation(studyTeamResponse, dispatch);

        const studyMetadataResponse = await updateStudyMetadata(storeState, existingStudyId);
        validateResolvedMutation(studyMetadataResponse, dispatch);

        if (status === 'draft') {
            dispatch(addApplicationMessage('success', 'Changes made to draft study saved.'));
            return browserHistory.push(nextLocation || '/studies');
        }

        if (status === 'activate') {
            await activateStudy(storeState, existingStudyId, dispatch);
            return browserHistory.push(`/study/${existingStudyId}/dashboard`);
        }
    };
}

function validateResolvedMutation(response, dispatch) {
    if (response && response.errors) {
        dispatch(addApplicationMessage('error', 'An error occurred when saving.'));
        return;
    }
}

function isEmptyDetails(state) {
    const details = lodash.get(state, 'study.fields.studyDetails', []);

    if (details.length === 0) {
        return true;
    }
    return details.some(detail => !detail.value);
}

/**
 * Updates study object. Can only update the name of the study. 
 * Cannot change study type after study is initially saved.
 * @param {Object} state Redux state
 * @param {String} existingStudyId
 */
const updateStudyMutation = async (state, existingStudyId) => {
    const existingStudyType = lodash.get(state, 'study.originalFields.studyType');
    const existingStageId = lodash.get(state, 'study.originalFields.studyStage.id');
    const existingName = lodash.get(state, 'study.originalFields.studyName');
    const name = lodash.get(state, 'study.fields.studyName');
    const institutionId = userDefaultInstitutionIdSelector(state);
    const currentStageId = lodash.get(state, 'study.fields.studyStage.id');
    const isMissingDetail = isEmptyDetails(state);

    // Look for any changes. If no changes, just return.
    if (existingName === name && existingStageId === currentStageId) {
        return;
    }

    const input = {
        id: existingStudyId,
        name,
        institutionId,
        // Cannot update studyTypeId.
        studyTypeId: existingStudyType.id,
        currentStageId,
    };
    const studyMutation = nodeWithArgs('updateStudy', {
        institutionId,
        isMissingDetail,
        input
    }, [
        'id',
        'institutionId',
        'name',
        'studyTypeId',
        'currentStageId',
    ]);

    return mutateGraphQL(toMutation(studyMutation));
};

/**
 * If changes to study team have been made, apply them.
 * @param {Object} state Redux state
 * @param {String} existingStudyId
 */
const updateStudyTeam = async (state, existingStudyId) => {
    const institutionId = userDefaultInstitutionIdSelector(state);

    // Look for changes among study team. If no changes, just return.
    const {
        studyTeamMembersToAdd,
        studyTeamMembersToRemove,
        studyTeamMembersToUpdate,
    } = getStudyTeamChanges(state);

    if (lodash.isEmpty(studyTeamMembersToAdd)
        && lodash.isEmpty(studyTeamMembersToRemove)
        && lodash.isEmpty(studyTeamMembersToUpdate)) {
        return;
    }

    const updatedStudyTeamMembersToRemove = studyTeamMembersToRemove.map(member => {
        const newMember = lodash.clone(member);
        delete newMember['studyRole'];
        delete newMember['user'];
        return newMember;
    });

    const studyTeamMutation = nodeWithArgs('updateStudyTeam', {
        institutionId,
        studyId: existingStudyId,
        studyTeamMembersToAdd,
        studyTeamMembersToRemove: updatedStudyTeamMembersToRemove,
        studyTeamMembersToUpdate,
    }, ['id']);
    return mutateGraphQL(toMutation(studyTeamMutation));
};


/**
 * If changes to study metadata have been made, apply them.
 * @param {Object} state Redux state
 * @param {String} existingStudyId
 */
const updateStudyMetadata = async (state, existingStudyId) => {
    const institutionId = userDefaultInstitutionIdSelector(state);

    const { studyMetadataToAdd, studyMetadataToRemove } = getStudyMetadataChanges(state);

    if (lodash.isEmpty(studyMetadataToAdd) && lodash.isEmpty(studyMetadataToRemove)) {
        return;
    }

    const studyMetadataMutation = nodeWithArgs('updateStudyMetadata', {
        institutionId,
        studyId: existingStudyId,
        studyMetadataToAdd,
        studyMetadataToRemove,
    }, ['id']);
    return mutateGraphQL(toMutation(studyMetadataMutation));
};





export function formatMetadataValue(inputFieldType, dataType, value) {
    const SELECT_INPUT_TYPES = ['single-select', 'multi-select'];
    const metadataValue = SELECT_INPUT_TYPES.includes(inputFieldType) ? value.value : value;

    return getStringValue(dataType, metadataValue);
}

/**
 * Translate a user to a study team member
 * @param {Array<{id:String, sub:String, displayName:String}>} users 
 * @param {String} institutionId 
 * @param {String} studyId 
 */
export function formatStudyTeamMembers(users, institutionId, studyId) {
    return users.map(user => {
        const team = {
            nameOfResearcher: user.nameOfResearcher,
            studyRoleId: user.studyRoleId,
            isClinicalStaff: false,
            userId: user.userId,
            institutionId,
            studyId,
            studyUserType: user.userType || STUDY_USER_TYPE.TEAM,
            sub: user.sub,
        };
        if (user.id) {
            team['id'] = user.id;
        }
        return team;
    });
}

export const deleteDraftStudy = (institutionId, studyId) => {
    return async (dispatch) => {
        const query = nodeWithArgs('removeDraftStudy', { institutionId, studyId });
        const response = await queryGraphQL(toMutation(query));
        if (response && response.errors) {
            dispatch(addApplicationMessage('error', 'Failed to Remove Study. Please try again later.'));
        } else {
            dispatch(addApplicationMessage('success', 'Draft study deleted.'));
        }
    };
};
